import React from 'react';
import { Box } from '@mui/material';

/** A straight link between two canvas entries, identified by id for live-drag tracking plus their current world-space positions. */
export type CanvasConnection = {
	fromId: string;
	toId: string;
	fromX: number;
	fromY: number;
	toX: number;
	toY: number;
};

/**
 * Animated dashed line connecting two canvas entries, in the same world-space coordinates as the canvas
 * cards (see `data-canvas-world` transform in CanvasView). `nodeRef` lets `buildCardHandlers`' `onDragMove`
 * update the line's transform/width imperatively while either endpoint is being dragged, the same way
 * anchor axis lines are kept live during drag (see `applyGridAxisPreviewDelta`) — re-rendering CanvasView
 * on every drag frame would undo the perf work `buildCardHandlers` is designed around.
 *
 * Generic by design — usable for any pair of connected canvas entries.
 */
export function CanvasConnectorLine({
	connection,
	nodeRef,
}: {
	connection: CanvasConnection;
	nodeRef: React.RefObject<HTMLDivElement | null>;
}) {
	const dx = connection.toX - connection.fromX;
	const dy = connection.toY - connection.fromY;
	const distance = Math.sqrt(dx * dx + dy * dy);
	const angle = Math.atan2(dy, dx) * (180 / Math.PI);
	return (
		<Box
			ref={nodeRef}
			aria-hidden
			sx={{
				position: 'absolute',
				left: '50%',
				top: '50%',
				height: 0,
				transformOrigin: '0 0',
				borderTop: (theme) => `2px dashed ${theme.palette.primary.main}`,
				pointerEvents: 'none',
				animation: 'canvasConnectorLinePulse 1.2s ease-in-out infinite',
				'@keyframes canvasConnectorLinePulse': {
					'0%': { opacity: 0.35 },
					'50%': { opacity: 0.85 },
					'100%': { opacity: 0.35 },
				},
			}}
			style={{
				width: distance,
				transform: `translate(${connection.fromX}px, ${connection.fromY}px) rotate(${angle}deg)`,
			}}
		/>
	);
}
